

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { globalCache, GlobalCacheItem } from './globalCache';

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'id-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 10);
}

interface ContentDB extends DBSchema {
  'content-pool': {
    key: string;
    value: {
      id: string;
      type: ContentType;
      content: any;
      difficulty: 'easy' | 'medium' | 'hard';
      createdAt: number;
      used: number;
    };
    indexes: { 'type': string; 'used': number; 'difficulty': string };
  };
  'config': {
    key: string;
    value: {
      key: string;
      value: any;
    };
  };
}

export type ContentType =
  | 'reading'
  | 'listening'
  | 'task1'
  | 'essay'
  | 'cueCard'
  | 'logic'
  | 'quickDrill'
  | 'vocabulary';

const DB_NAME = 'ted-talks-dz-content';
const DB_VERSION = 3;

const MIN_CACHE_SIZE = 3;
const CONTENT_EXPIRY = 7 * 24 * 60 * 60 * 1000;
const ERROR_COOLDOWN = 5 * 60 * 1000;

export class ContentPreloader {
  private db: IDBPDatabase<ContentDB> | null = null;
  private isInitialized = false;
  private isGenerating = false;
  private workerInterval: number | null = null;
  private rateLimitHit = false;
  private rateLimitResetTime = 0;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      this.db = await openDB<ContentDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (db.objectStoreNames.contains('content-pool')) {
            db.deleteObjectStore('content-pool');
          }
          if (db.objectStoreNames.contains('config')) {
            db.deleteObjectStore('config');
          }
          const poolStore = db.createObjectStore('content-pool', { keyPath: 'id' });
          poolStore.createIndex('type', 'type');
          poolStore.createIndex('used', 'used');
          poolStore.createIndex('difficulty', 'difficulty');
          db.createObjectStore('config', { keyPath: 'key' });
        },
      });

      console.log('🗄️ TED.TALKS.DZ: Neural Local Sync Active');
      await this.cleanExpiredContent();

      // We no longer aggressively pre-fill from Gemini here to save costs.
      // The background worker will mostly maintain the local cache for stats/health checks.
      this.startBackgroundWorker();
      this.isInitialized = true;
    } catch (error) {
      console.error('❌ TED.TALKS.DZ: Neural Sync Failure:', error);
      throw error;
    }
  }

  /**
   * THE HIERARCHY OF SPEED & COST EFFICIENCY
   * 1. Check Global Cache (Supabase) [Cost: $0]
   * 2. Fallback to Generator (Gemini) [Cost: $$$]
   * 3. Hydrate Global Cache (Push)
   */
  async getInstantContent(type: ContentType, difficulty: 'easy' | 'medium' | 'hard' = 'medium'): Promise<any | null> {
    if (!this.isInitialized) await this.initialize();

    try {
      // 1. Check Global Cache First
      // This is the "Shared Neural Pool" logic
      const globalItem = await globalCache.fetchCachedItem(type, difficulty);

      if (globalItem) {
        console.log(`🌐 Cache HIT: Served ${type} from Global Pool`);
        // We store it locally just so it appears in usage stats/history if needed
        await this.storeContent({
          id: globalItem.id,
          type: globalItem.type as ContentType,
          content: globalItem.content,
          difficulty: globalItem.difficulty as any,
          createdAt: Date.now(),
          used: 1
        });
        return globalItem.content;
      }

      console.warn(`⚠️ Cache MISS: Generating ${type} via Gemini Engine...`);

      // 2. Fallback to Generator
      const freshItem = await this.generateSingleContent(type, difficulty);

      if (freshItem) {
        // 3. Hydrate
        // Push to Supabase so the next user doesn't pay for generation
        await globalCache.cacheItem({
          id: freshItem.id,
          type: freshItem.type,
          difficulty: freshItem.difficulty,
          content: freshItem.content
        });

        // Store locally
        await this.storeContent(freshItem);
        return freshItem.content;
      }

      return null;
    } catch (error) {
      console.error("Content retrieval failed", error);
      return null;
    }
  }

  async getUnusedCount(): Promise<number> {
    if (!this.db) return 0;
    try {
      const tx = this.db.transaction('content-pool', 'readonly');
      const index = tx.store.index('used');
      return await index.count(0);
    } catch (e) {
      return 0;
    }
  }

  private async generateSingleContent(type: ContentType, difficulty: 'easy' | 'medium' | 'hard'): Promise<any> {
    try {
      // Call server-side generation API (secure)
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, difficulty })
      });

      if (!response.ok) {
        if (response.status === 429) {
          const data: { error?: string } = await response.json();
          throw new Error(data.error || 'Rate limit exceeded. Please wait.');
        }
        throw new Error('Generation failed');
      }

      const data: { content?: any } = await response.json();
      const content = data.content;

      if (!content) return null;

      return {
        id: generateId(),
        type,
        content,
        difficulty,
        createdAt: Date.now(),
        used: 0,
      };
    } catch (error) {
      console.error('Content generation failed:', error);
      throw error;
    }
  }

  private async storeContent(item: any): Promise<void> {
    if (!this.db || !item || !item.id) return;
    try {
      const tx = this.db.transaction('content-pool', 'readwrite');
      await tx.store.put(item);
      await tx.done;
    } catch (error) {
      console.error('IDB Store failed:', error);
    }
  }

  private async cleanExpiredContent(): Promise<void> {
    if (!this.db) return;
    try {
      const now = Date.now();
      const tx = this.db.transaction('content-pool', 'readwrite');
      const store = tx.store;
      let cursor = await store.openCursor();
      while (cursor) {
        if (now - cursor.value.createdAt > CONTENT_EXPIRY) {
          await cursor.delete();
        }
        cursor = await cursor.continue();
      }
      await tx.done;
    } catch (e) { }
  }

  private startBackgroundWorker(): void {
    // We keep the worker minimal, just to keep the connection alive or do cleanup.
    // We do NOT aggressively generate content here anymore to save API costs.
    if (this.workerInterval) return;
    this.workerInterval = window.setInterval(async () => {
      if (this.rateLimitHit && Date.now() > this.rateLimitResetTime) {
        this.rateLimitHit = false;
      }
      await this.cleanExpiredContent();
    }, 5 * 60 * 1000);
  }

  stopBackgroundWorker(): void {
    if (this.workerInterval) {
      clearInterval(this.workerInterval);
      this.workerInterval = null;
    }
  }

  async getStats(): Promise<any> {
    if (!this.db) return null;
    try {
      const tx = this.db.transaction('content-pool', 'readonly');
      const all = await tx.store.getAll();
      const unused = all.filter(i => i.used === 0);
      const byType = unused.reduce((acc, curr) => {
        acc[curr.type] = (acc[curr.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return { total: all.length, unused: unused.length, byType };
    } catch (e) {
      return null;
    }
  }
}

export const contentPreloader = new ContentPreloader();
